import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from './firebase';
import { usePlan } from './usePlan';
import { useUserDoc } from './useUserDoc';
import { DashboardLens, dashboardLensLabel, resolveDashboardLens } from './dashboardLens';
import { recordLensLanding } from './lensMetrics';
import { PlanType, planDisplayName, planCanViewRollup, planCanBrowseMinutesOnWeb, webDisplayPlan } from './plan';
import { Btn, Icon, LogoMark } from './components/primitives';
import LoginPage from './pages/LoginPage';
import ProjectListPage from './pages/ProjectListPage';
import FocusPipelinePage from './pages/FocusPipelinePage';
import ProjectDashboardPage from './pages/ProjectDashboardPage';
import MinutesDetailPage from './pages/MinutesDetailPage';
import CompanyTimelinePage from './pages/CompanyTimelinePage';
import CompanyMasterPage from './pages/CompanyMasterPage';
import CalendarImportPage from './pages/CalendarImportPage';
import PricingPage from './pages/PricingPage';
import { Company } from './useCompanies';
import { Theme, useTheme } from './theme';
import './App.css';

type Screen = 'projects' | 'dashboard' | 'minutes' | 'companyTimeline' | 'companyMaster' | 'calendarImport';

// 最上位タブ（S-056 新IA / DEC-054）。既定はダッシュボード（Focus＋Pipeline）。
// 会社別・プロジェクト別は既存 ProjectListPage をモード固定で接続する。
type RootTab = 'dashboard' | 'company' | 'project';

interface Project {
  id: string;
  name: string;
  description?: string;
  updatedAt?: any;
}

interface Meeting {
  id: string;
  title: string;
  recordedAt?: any;
  duration?: number;
  status?: string;
  companyIds?: string[];
  tags?: string[];
}

// 画面遷移を 1 本のナビゲーションスタックで表す（ブラウザ履歴と同期）。
// history.state には Firestore オブジェクト（Timestamp 等）を入れず深さ(vlDepth)のみ保持し、
// 実データはこの React スタックが持つ。popstate で深さに合わせてスタックを切り戻す。
interface NavEntry {
  screen: Screen;
  project?: Project | null;
  meeting?: Meeting | null;
  company?: Company | null;
  scrollY?: number;
  // S-058-7: 議事録全文で強調表示する逐語（まとめの引用タップ由来・任意）。
  highlight?: string;
}

// プラン別の表示メタ（アカウントメニュー内のリッチ表示用）。
const planMeta: Record<PlanType, { tone: string; bg: string; border: string; icon: string; desc: string }> = {
  standard: { tone: 'var(--amber-400)', bg: 'var(--cta-fill)', border: 'var(--cta-line)', icon: 'sparkles', desc: 'オフライン無制限＋AI機能フル開放' },
  light: { tone: 'var(--teal-300)', bg: 'var(--accent-fill)', border: 'var(--accent-line)', icon: 'wifi-off', desc: 'オフライン議事録 作成無制限' },
  free: { tone: 'var(--fg-secondary)', bg: 'var(--overlay-soft)', border: 'var(--border-strong)', icon: 'mic', desc: '作成無制限・最新2件まで閲覧' },
};

// アカウントメニュー内の共通アクション行。
const MenuItem: React.FC<{ icon: string; label: string; onClick: () => void; disabled?: boolean }> = ({ icon, label, onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
    padding: '10px 12px', borderRadius: 'var(--r-2)', cursor: disabled ? 'wait' : 'pointer',
    background: 'transparent', border: 'none', color: 'var(--fg-secondary)',
    font: 'var(--type-ui)', fontWeight: 500,
  }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--bg-hover)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
    <Icon name={icon} size={16} style={{ color: 'var(--fg-tertiary)' }} />
    {label}
  </button>
);

// グローバルトップバー。
// レスポンシブ方針: 上部バーには「ブランド（縦積み）」と「アカウントアイコン」だけを置き、
// プラン表示・相手先・プラン管理・ログアウトはすべてアカウントメニューに収納する。
// これにより狭い画面でも横はみ出しが構造的にゼロになる（要素が常に 2 つだけ）。
const TopBar: React.FC<{
  user: User;
  plan: PlanType;
  planLoading: boolean;
  lens: DashboardLens;
  lensReady: boolean;
  onChangeLens: (lens: DashboardLens) => void;
  onHome: () => void;
  onManageCompanies: () => void;
  onOpenCalendarImport: () => void;
}> = ({ user, plan, planLoading, lens, lensReady, onChangeLens, onHome, onManageCompanies, onOpenCalendarImport }) => {
  const [portalLoading, setPortalLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);

  // メニュー外クリック / Esc で閉じる。
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  const handleManagePlan = async () => {
    setPortalLoading(true);
    try {
      const fn = httpsCallable<{}, { url: string }>(functions, 'createCustomerPortalSession');
      const result = await fn({});
      window.open(result.data.url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      alert('プラン管理ページを開けませんでした: ' + (e.message || e));
    } finally {
      setPortalLoading(false);
      setMenuOpen(false);
    }
  };

  const initial = (user.displayName || user.email || '？').trim().charAt(0).toUpperCase();
  const pm = planMeta[plan] || planMeta.free;

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50, height: 56,
      display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
      background: 'var(--bar-bg)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid var(--border)',
    }}>
      {/* ブランド（縦積み: VoiLog / ダッシュボード） */}
      <button onClick={onHome} style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', cursor: 'pointer', padding: 0, minWidth: 0 }}>
        <LogoMark size={26} />
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
          <span style={{ font: '700 15px/1.1 var(--font-display)', color: 'var(--fg)', letterSpacing: '-0.01em' }}>VoiLog</span>
          <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', fontWeight: 500, marginTop: 3, letterSpacing: '0.02em' }}>dashboard</span>
        </span>
      </button>

      {/* ロゴ右横の控えめな URL コピー導線（PC で開く用 / DEC-060）。 */}
      <PcAccessLink />

      <div style={{ flex: 1 }} />

      {/* アカウントメニュー（プラン表示＋ナビをすべて収納） */}
      <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button onClick={() => setMenuOpen((v) => !v)} aria-haspopup="menu" aria-expanded={menuOpen} title="アカウント" style={{
          width: 34, height: 34, borderRadius: 999, background: 'var(--navy-600)',
          border: '1px solid ' + (menuOpen ? 'var(--border-accent)' : 'var(--border-strong)'),
          display: 'grid', placeItems: 'center', cursor: 'pointer',
          font: '600 13px/1 var(--font-sans)', color: 'var(--fg-secondary)', padding: 0,
        }}>{initial}</button>

        {menuOpen && (
          <div role="menu" style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 264, maxWidth: 'calc(100vw - 24px)',
            background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
            borderRadius: 'var(--r-3)', boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            padding: 12, zIndex: 60, display: 'flex', flexDirection: 'column', gap: 4,
          }}>
            {/* ユーザー情報 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 10px' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 999, background: 'var(--navy-600)',
                border: '1px solid var(--border-strong)', display: 'grid', placeItems: 'center',
                font: '600 14px/1 var(--font-sans)', color: 'var(--fg-secondary)', flexShrink: 0,
              }}>{initial}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ font: 'var(--type-ui)', fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.displayName || 'ユーザー'}
                </div>
                <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </div>
              </div>
            </div>

            {/* プラン（リッチ表示） */}
            {!planLoading && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', marginBottom: 2,
                borderRadius: 'var(--r-2)', background: pm.bg, border: '1px solid ' + pm.border,
              }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, display: 'grid', placeItems: 'center', background: 'var(--overlay-soft)', flexShrink: 0 }}>
                  <Icon name={pm.icon} size={16} style={{ color: pm.tone }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ font: '700 14px/1.2 var(--font-sans)', color: pm.tone }}>{planDisplayName(plan)}プラン</div>
                  <div style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', marginTop: 2 }}>{pm.desc}</div>
                </div>
              </div>
            )}

            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />

            <MenuItem icon="building" label="相手先" onClick={() => { setMenuOpen(false); onManageCompanies(); }} />
            <MenuItem icon="calendar" label="カレンダー取込" onClick={() => { setMenuOpen(false); onOpenCalendarImport(); }} />
            <MenuItem icon="arrow-up-circle" label={portalLoading ? 'プラン管理を開いています…' : 'プラン管理'} onClick={handleManagePlan} disabled={portalLoading} />

            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />

            {/* テーマ切替（ダーク／明るい）。選択は localStorage にユーザーごと永続化。既定=ダーク。 */}
            <div style={{ padding: '8px 12px 6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, font: 'var(--type-ui)', fontWeight: 500, color: 'var(--fg-secondary)' }}>
                <Icon name="palette" size={16} style={{ color: 'var(--fg-tertiary)' }} />
                テーマ
              </div>
              <div role="radiogroup" aria-label="テーマ" style={{ display: 'flex', gap: 6 }}>
                {([['dark', 'ダーク'], ['light', '明るい']] as [Theme, string][]).map(([value, label]) => {
                  const active = theme === value;
                  return (
                    <button key={value} role="radio" aria-checked={active} onClick={() => setTheme(value)} style={{
                      flex: 1, padding: '8px 10px', borderRadius: 'var(--r-2)', cursor: 'pointer',
                      font: 'var(--type-ui)', fontWeight: 600,
                      background: active ? 'var(--accent-fill)' : 'transparent',
                      border: '1px solid ' + (active ? 'var(--border-accent)' : 'var(--border-strong)'),
                      color: active ? 'var(--accent)' : 'var(--fg-secondary)',
                      transition: 'all var(--dur-fast) var(--ease)',
                    }}>{label}</button>
                  );
                })}
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />

            {/* 表示レンズ切替（S-061-4）。建築（物件まとめ）⇔ 営業（約束ベース）を本人が選ぶ。
                選択は users/{uid}.dashboardLens に永続化し、トップの着地画面を決める。
                Flutter 側に切替UIは無いため、建築↔営業の切替は本トグルが唯一の手段（MVP必須）。 */}
            {lensReady && (
              <div style={{ padding: '8px 12px 6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, font: 'var(--type-ui)', fontWeight: 500, color: 'var(--fg-secondary)' }}>
                  <Icon name="layout-grid" size={16} style={{ color: 'var(--fg-tertiary)' }} />
                  表示
                </div>
                <div role="radiogroup" aria-label="ダッシュボード表示" style={{ display: 'flex', gap: 6 }}>
                  {(['focus', 'construction'] as DashboardLens[]).map((value) => {
                    const active = lens === value;
                    return (
                      <button key={value} role="radio" aria-checked={active} onClick={() => onChangeLens(value)} style={{
                        flex: 1, padding: '8px 10px', borderRadius: 'var(--r-2)', cursor: 'pointer',
                        font: 'var(--type-ui)', fontWeight: 600, whiteSpace: 'nowrap',
                        background: active ? 'var(--accent-fill)' : 'transparent',
                        border: '1px solid ' + (active ? 'var(--border-accent)' : 'var(--border-strong)'),
                        color: active ? 'var(--accent)' : 'var(--fg-secondary)',
                        transition: 'all var(--dur-fast) var(--ease)',
                      }}>{dashboardLensLabel(value)}</button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            <MenuItem icon="arrow-left" label="ログアウト" onClick={() => { setMenuOpen(false); signOut(auth); }} />
          </div>
        )}
      </div>
    </div>
  );
};

// 最上位タブバー（ダッシュボード / 会社別 / プロジェクト別）。ルート（深さ0）でのみ表示。
const RootTabs: React.FC<{ tab: RootTab; onChange: (t: RootTab) => void }> = ({ tab, onChange }) => {
  const tabs: { id: RootTab; label: string }[] = [
    { id: 'dashboard', label: 'ダッシュボード' },
    { id: 'company', label: '会社別' },
    { id: 'project', label: 'プロジェクト別' },
  ];
  return (
    <div style={{
      position: 'sticky', top: 56, zIndex: 30,
      background: 'var(--bar-bg)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid var(--border)',
    }}>
      <nav role="tablist" aria-label="表示切替" style={{
        maxWidth: 1180, margin: '0 auto', padding: '10px 22px 0', display: 'flex', gap: 4,
      }}>
        {tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} role="tab" aria-selected={active} onClick={() => onChange(t.id)} style={{
              appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', background: 'transparent',
              border: 'none', borderBottom: '2px solid ' + (active ? 'var(--teal-400)' : 'transparent'),
              padding: '10px 18px 13px', font: 'var(--type-ui)', fontWeight: 700, whiteSpace: 'nowrap',
              color: active ? 'var(--fg)' : 'var(--fg-tertiary)',
              transition: 'color var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease)',
            }}>{t.label}</button>
          );
        })}
      </nav>
    </div>
  );
};

// ロゴ横の控えめな「URL をコピー」リンク。アプリ内 WebView で開いたユーザーが
// この URL を PC へ送って閲覧を続けられるようにする（WebView→メール/チャットでPCへ送る想定）。
// URL は配信元オリジン（window.location.origin）＝WebView でも同じホスティングURLになるため
// ハードコードしない（環境・チャネルが変わっても自動追従）。
// 旧 PcAccessBar（ダッシュボード上部の大バナー）から TopBar 内の小導線へ移設（DEC-060 配置変更）。
const PcAccessLink: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const url = window.location.origin;
  const copy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // 古い WebView 等で Clipboard API が無い場合のフォールバック。
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // コピー失敗してもユーザー操作を阻害しない（控えめな補助導線）。
      console.error('[PcAccessLink] copy failed:', e);
    }
  };
  return (
    <button
      onClick={copy}
      title={copied ? 'コピーしました' : 'PCで開く用にこのURLをコピー: ' + url}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
        padding: '5px 9px', borderRadius: 999, cursor: 'pointer',
        background: 'transparent', border: '1px solid var(--border)',
        font: 'var(--type-caption)', fontWeight: 600,
        color: copied ? 'var(--teal-300)' : 'var(--fg-tertiary)',
        transition: 'all var(--dur-fast) var(--ease)', whiteSpace: 'nowrap',
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} size={13} style={{ flexShrink: 0 }} />
      <span>{copied ? 'コピー済み' : 'URLをコピー'}</span>
    </button>
  );
};

// Web 議事録ビューのアップグレード誘導オーバーレイ（planCanBrowseMinutesOnWeb=false 時 = free / light）。
// 議事録一覧は背後で実描画し blur でぼかす（誘導目的のプレビュー）。本コンポーネントはその上に重ねる
// 半透明オーバーレイで、スタンダードへのアップグレード誘導だけを見せる。
// Web ダッシュボードはスタンダード（¥980/月）専用の「マルチデバイス」特典。ライトは「端末で完結」コンセプト。
// プラン別に文面を出し分ける（free は作成・閲覧がアプリで使える点を、light は端末完結プランである点を添える）。
// DEC-009 ガード: %・点数・的中率・精度訴求・煽りは出さない。素朴な事実とプラン名のみ。
//
// レイアウト: TopBar（高さ56・z50）の直下を覆う fixed オーバーレイ。pointerEvents:none で
// スクロールは背後のぼかし一覧へ透過させ（スクロールはできるが読めない状態）、誘導カードだけ
// pointerEvents:auto にしてボタンを操作可能にする。
// NOTE: 2026-06-19 でかいオーバーレイをやめ、ぼかし一覧 + 上部の小バナー誘導に変更（render 側）。
// 本コンポーネントは現在レンダリングしていないが、将来の誘導 UI 用に残す。
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MinutesWebUpsell: React.FC<{ plan: PlanType; onSeePlans: () => void }> = ({ plan, onSeePlans }) => (
  <div style={{
    position: 'fixed', top: 56, left: 0, right: 0, bottom: 0, zIndex: 40,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '72px 24px 24px',
    background: 'linear-gradient(180deg, var(--scrim-top), var(--scrim-bottom))',
    pointerEvents: 'none', // 背後のぼかし一覧へスクロールを透過させる
  }}>
    {/* 半透明フロスト面: 背後のぼかし一覧が透けて見えつつ、誘導テキストは読める。
        ロックアイコンは出さず、SaaS 的な「プレビュー + 有料動線」の見え方にする。 */}
    <div style={{
      maxWidth: 460, textAlign: 'center', pointerEvents: 'auto',
      padding: 28, borderRadius: 'var(--r-3)',
      background: 'var(--upsell-panel-bg)',
      border: '1px solid var(--upsell-panel-line)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
    }}>
      <h1 style={{ font: 'var(--type-h2)', color: 'var(--fg)', marginBottom: 12, textShadow: '0 1px 8px rgba(0,0,0,0.25)' }}>
        議事録はアプリでご確認ください
      </h1>
      <p style={{ font: 'var(--type-body)', color: 'var(--fg)', lineHeight: 1.8, marginBottom: 8, textShadow: '0 1px 6px rgba(0,0,0,0.20)' }}>
        Web ダッシュボードで議事録を閲覧するには、スタンダードプラン（¥980/月）へのアップグレードが必要です。
      </p>
      <p style={{ font: 'var(--type-body)', color: 'var(--fg)', lineHeight: 1.8, marginBottom: 24, textShadow: '0 1px 6px rgba(0,0,0,0.20)' }}>
        {plan === 'light'
          ? 'ライトプランは「端末で完結」プランです。議事録の作成・閲覧はアプリでいつでもご利用いただけます。スタンダードにアップグレードすると、ブラウザの Web ダッシュボードでも議事録を確認できます。'
          : '無料プランでも、議事録の作成と閲覧はアプリでいつでもご利用いただけます。'}
      </p>
      <Btn kind="cta" icon="arrow-up-circle" onClick={onSeePlans}>プランを見る</Btn>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  // 最上位タブ（ルートのみ）。レンズ解決までは null（＝未確定）にして、
  // 解決後に建築レンズなら 'project'、それ以外は 'dashboard' を着地させる（S-061-4/6）。
  // null の間はルート本体をマウントせずスケルトンを出す（チラつき・直列二重read排除）。
  const [rootTab, setRootTab] = useState<RootTab | null>(null);

  // ナビゲーションスタック（先頭=ルート=案件一覧）。
  const [stack, setStack] = useState<NavEntry[]>([{ screen: 'projects' }]);
  const stackRef = useRef<NavEntry[]>(stack);
  useEffect(() => { stackRef.current = stack; }, [stack]);
  const current = stack[stack.length - 1];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  // 端末/ブラウザの戻るボタンと同期する。
  // ルートに深さ 0 を刻み、forward 遷移ごとに pushState(vlDepth) を積む。
  // popstate では history.state の深さに合わせてスタックを切り戻し、スクロール位置を復元する。
  useEffect(() => {
    window.history.replaceState({ vlDepth: 0 }, '');
    const onPop = (e: PopStateEvent) => {
      const d = e.state && typeof e.state.vlDepth === 'number' ? e.state.vlDepth : 0;
      setStack((prev) => {
        if (d + 1 >= prev.length) return prev; // 進む方向 or 同一深さは無視
        const next = prev.slice(0, d + 1);
        const top = next[next.length - 1];
        requestAnimationFrame(() => window.scrollTo(0, top?.scrollY ?? 0));
        return next;
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // S-051-1h: ログインユーザーのプランを購読し、各画面へ出し分け情報を渡す。
  const { plan, loading: planLoading } = usePlan(user?.uid);

  // S-061-4: ダッシュボードレンズ（営業=focus / 建築=construction）を購読・解決する。
  const { dashboardLens, industry, loading: userDocLoading } = useUserDoc(user?.uid);
  const lens = resolveDashboardLens(dashboardLens, industry);
  const lensReady = !!user && !userDocLoading;

  // レンズ確定 → 着地タブを決める（S-061-6）。建築レンズは「プロジェクト別」、それ以外は
  // 現行どおり「ダッシュボード（約束Focus）」。トグルで lens が変わったら着地も追従する。
  // 依存は lens 文字列の変化のみ（手動のタブ移動は lens 不変なので保持される）。
  useEffect(() => {
    if (!lensReady) return;
    setRootTab(lens === 'construction' ? 'project' : 'dashboard');
  }, [lensReady, lens]);

  // 計測: レンズ別ランディング表示（lens_landing）。lens が確定するたびに1回記録する。
  useEffect(() => {
    if (!lensReady || !user) return;
    recordLensLanding(user.uid, lens);
  }, [lensReady, lens, user]);

  // S-061-4: トグルでレンズを切り替える（users/{uid}.dashboardLens を更新）。
  // 反映は useUserDoc の onSnapshot が担い、着地タブが追従する。
  const changeLens = (next: DashboardLens) => {
    if (!user) return;
    if (next === lens) return;
    updateDoc(doc(db, 'users', user.uid), { dashboardLens: next }).catch((e) => {
      console.error('[App] failed to update dashboardLens:', e);
    });
  };

  // 建築レンズ × 有料プランのときだけ、プロジェクト一覧に未完バッジを出す（S-061-5/6）。
  // 営業・未設定ユーザー、および無料プランには出さない（無風＋有料ゲート＋read増ゼロ）。
  // プラン解決前は楽観的に非表示（解決後に有料なら出す。誤って課金者の read を増やさない）。
  // Web 表示では free を light と同一にする（webDisplayPlan）。まとめ（未完バッジ）も free に出す。
  const showUnresolvedBadges = lens === 'construction' && !planLoading && planCanViewRollup(webDisplayPlan(plan));

  // forward 遷移: 離脱画面のスクロール位置を退避し、新エントリを積んで履歴へ push。
  const push = (entry: NavEntry) => {
    const prev = stackRef.current;
    const depth = prev.length; // 新エントリの深さ
    const updatedPrev = prev.slice();
    updatedPrev[updatedPrev.length - 1] = { ...updatedPrev[updatedPrev.length - 1], scrollY: window.scrollY };
    const next = [...updatedPrev, entry];
    stackRef.current = next;
    setStack(next);
    window.history.pushState({ vlDepth: depth }, '');
    window.scrollTo(0, 0);
  };

  // 1 つ前へ戻る（画面内「戻る」ボタンも端末戻るも同じ popstate 経路に集約）。
  // ルート（深さ0）では history.back() を呼ばない（アプリ外へ抜けるのを防ぐ。goHome と同じガード）。
  const goBack = () => {
    if (stackRef.current.length <= 1) { window.scrollTo(0, 0); return; }
    window.history.back();
  };

  // ルート（案件一覧）まで一気に戻る（ロゴ）。
  const goHome = () => {
    const depth = stackRef.current.length - 1;
    if (depth <= 0) { window.scrollTo(0, 0); return; }
    window.history.go(-depth);
  };

  if (window.location.pathname === '/pricing') {
    return <PricingPage />;
  }

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', color: 'var(--fg-tertiary)' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><LogoMark size={40} /></div>
          <div style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>読み込み中...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // 議事録選択（ダッシュボード/相手先タイムライン共通）。親画面はスタックが覚えている。
  // S-058-7: highlight（逐語）が渡れば議事録全文で該当箇所をスクロール＆強調する。
  const handleSelectMeeting = (meeting: Meeting, highlight?: string) => {
    const top = stackRef.current[stackRef.current.length - 1];
    push({ screen: 'minutes', project: top.project, company: top.company, meeting, highlight });
  };

  // 議事録ビュー領域（ホーム/案件ダッシュ/相手先タイムライン/議事録詳細）か否か。
  // 相手先マスタ・カレンダー取込（アカウントメニュー由来）は議事録ビューではないので対象外。
  const isMinutesArea =
    current.screen === 'projects' ||
    current.screen === 'dashboard' ||
    current.screen === 'minutes' ||
    current.screen === 'companyTimeline';
  // free / light（planCanBrowseMinutesOnWeb=false / DEC-059）は Web で議事録の「中身」を見せない。
  // ただし領域全体はぼかさず通常表示し（プロジェクト名・会議タイトル・日時は読める）、
  // 議事録の本文・プレビューだけを各ページ側でモザイク化する（webMinutesLocked を委譲）。
  // 上部には小さなアップグレード誘導バナーを置く。
  // プラン解決前（planLoading）は判定保留＝本文/一覧を読み込まずローディング。
  const minutesAreaChecking = isMinutesArea && planLoading;
  const minutesAreaLocked = isMinutesArea && !planLoading && !planCanBrowseMinutesOnWeb(plan);
  const minutesAreaVisible = isMinutesArea && !minutesAreaChecking;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar user={user} plan={plan} planLoading={planLoading}
        lens={lens} lensReady={lensReady} onChangeLens={changeLens} onHome={goHome}
        onManageCompanies={() => push({ screen: 'companyMaster' })}
        onOpenCalendarImport={() => push({ screen: 'calendarImport' })} />

      {/* アカウントメニュー由来（議事録ビュー外）。プラン無関係で常に通常表示。 */}
      {current.screen === 'companyMaster' && (
        <CompanyMasterPage user={user} onBack={goBack} />
      )}

      {current.screen === 'calendarImport' && (
        <CalendarImportPage user={user} onBack={goBack} plan={plan} planLoading={planLoading} />
      )}

      {/* プラン解決待ち: 議事録ビューは本文/一覧を読み込まずローディング。 */}
      {minutesAreaChecking && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 24px', color: 'var(--fg-tertiary)' }}>
          <div style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>読み込み中...</div>
        </div>
      )}

      {/* 議事録ビュー領域。一覧・タイトル・日時は全プラン通常表示。free / light は
          議事録の「中身」（本文・プレビュー）だけを各ページがモザイク化する（DEC-059）。 */}
      {minutesAreaVisible && (
        <div style={{ position: 'relative' }}>
          {/* free / light: ぼかし一覧の上部に小さなアップグレード誘導バナーだけ置く
              （でかいオーバーレイで覆わない / DEC-059）。バナーはぼかさず操作可能。 */}
          {minutesAreaLocked && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 12, flexWrap: 'wrap',
              margin: '12px 16px', padding: '10px 16px',
              borderRadius: 'var(--r-2)',
              background: 'var(--upsell-panel-bg)',
              border: '1px solid var(--upsell-panel-line)',
              font: 'var(--type-caption)', color: 'var(--fg-secondary)',
            }}>
              <span>Web で議事録を閲覧するにはスタンダードプラン（¥980/月）が必要です。</span>
              <a
                href="/pricing"
                style={{ color: 'var(--accent)', font: 'var(--type-ui)', textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                プランを見る →
              </a>
            </div>
          )}
          {/* 議事録ビュー領域は通常表示（一覧・タイトル・日時は読める）。
              free / light は各ページが議事録の「中身」だけをモザイク化する
              （MinutesDetailPage 本文 / タイムラインカードのプレビュー / DEC-059）。 */}
          <div>
            {/* 最上位タブはルート（案件ホーム）でのみ・レンズ解決後に表示する。
                建築レンズ（construction）はプロジェクト別ビュー専用なのでタブバー自体を出さない
                （営業系タブ＝ダッシュボード/会社別/プロジェクト別の切替は営業=focus レンズ限定）。 */}
            {current.screen === 'projects' && rootTab !== null && lens !== 'construction' && (
              <RootTabs tab={rootTab} onChange={setRootTab} />
            )}

            {current.screen === 'minutes' && current.project && current.meeting && (
              <MinutesDetailPage
                user={user}
                project={current.project}
                meeting={current.meeting}
                onBack={goBack}
                highlight={current.highlight}
                webMinutesLocked={minutesAreaLocked}
              />
            )}

            {current.screen === 'companyTimeline' && current.project && current.company && (
              <CompanyTimelinePage
                user={user}
                project={current.project}
                company={current.company}
                onBack={goBack}
                onSelectMeeting={(meeting) => handleSelectMeeting(meeting as Meeting)}
                plan={plan}
                planLoading={planLoading}
                webMinutesLocked={minutesAreaLocked}
              />
            )}

            {current.screen === 'dashboard' && current.project && (
              <ProjectDashboardPage
                user={user}
                project={current.project}
                onBack={goBack}
                onSelectMeeting={(meeting, highlight) => handleSelectMeeting(meeting, highlight)}
                plan={plan}
                planLoading={planLoading}
                webMinutesLocked={minutesAreaLocked}
              />
            )}

            {/* レンズ解決待ち: ルート本体はマウントせずスケルトンを出す（チラつき排除 / S-061-4）。 */}
            {current.screen === 'projects' && rootTab === null && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 24px', color: 'var(--fg-tertiary)' }}>
                <div style={{ font: 'var(--type-body)', color: 'var(--fg-secondary)' }}>読み込み中...</div>
              </div>
            )}

            {/* ルート＝最上位タブ。営業レンズ既定はダッシュボード（Focus＋Pipeline）。 */}
            {current.screen === 'projects' && rootTab === 'dashboard' && (
              <FocusPipelinePage
                user={user}
                onOpenProject={(projectId, projectName) =>
                  push({ screen: 'dashboard', project: { id: projectId, name: projectName || '' } })}
                onOpenMeeting={(meeting, projectId, projectName) =>
                  push({
                    screen: 'minutes',
                    project: { id: projectId, name: projectName },
                    meeting: { id: meeting.id, title: meeting.title, recordedAt: meeting.recordedAt },
                  })}
              />
            )}

            {current.screen === 'projects' && (rootTab === 'company' || rootTab === 'project') && (
              <ProjectListPage
                user={user}
                forcedViewMode={rootTab === 'company' ? 'company' : 'project'}
                showUnresolvedBadges={rootTab === 'project' && showUnresolvedBadges}
                onSelectProject={(project) => push({ screen: 'dashboard', project })}
                onSelectCompany={(project, company) => push({ screen: 'companyTimeline', project, company })}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
